import { Box, Typography } from "@mui/material";
import { PieChart } from "@mui/x-charts/PieChart";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import ChartCard from "./ChartCard";
import {
  getOutsidePieSeriesProps,
  getOutsidePieSlotProps,
  getOutsidePieSlots,
} from "./PieArcLabelWithConnector";
import { COMPLAINT_CLASSIFICATION } from "../../utils/complaintClassification";

const ComplaintClassificationPieChart = ({ data, isRTL }) => {
  const { t } = useTranslation();

  const chartData = useMemo(() => {
    const unclassified =
      data?.find((d) => d.classification === COMPLAINT_CLASSIFICATION.UNCLASSIFIED)
        ?.count ?? 0;
    const valid =
      data?.find((d) => d.classification === COMPLAINT_CLASSIFICATION.VALID)
        ?.count ?? 0;
    const malicious =
      data?.find((d) => d.classification === COMPLAINT_CLASSIFICATION.MALICIOUS)
        ?.count ?? 0;

    return [
      {
        id: 0,
        value: unclassified,
        label: t("complaintClassification.unclassified"),
        color: "#9e9e9e",
      },
      {
        id: 1,
        value: valid,
        label: t("complaintClassification.valid"),
        color: "#2e7d32",
      },
      {
        id: 2,
        value: malicious,
        label: t("complaintClassification.malicious"),
        color: "#d32f2f",
      },
    ].filter((item) => item.value > 0);
  }, [data, t]);

  const total = chartData.reduce((sum, item) => sum + item.value, 0);

  return (
    <ChartCard
      title={t("reports.charts.complaintClassification")}
      isRTL={isRTL}
      height={400}
      exportCategories={chartData.map((item) => item.label)}
      exportSeries={[
        { name: t("reports.charts.ticketCount"), data: chartData.map((item) => item.value) },
      ]}
    >
      {(layoutHeight, expanded, showLabels) => {
        const outerRadius = expanded ? 120 : 72;
        return total === 0 ? (
          <Box
            sx={{
              height: layoutHeight,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography color="text.secondary">{t("reports.noData")}</Typography>
          </Box>
        ) : (
          <PieChart
            series={[
              {
                data: chartData,
                innerRadius: expanded ? 50 : 28,
                outerRadius,
                paddingAngle: 2,
                cornerRadius: 4,
                highlightScope: { fade: "global", highlight: "item" },
                ...getOutsidePieSeriesProps(outerRadius, showLabels),
              },
            ]}
            height={layoutHeight}
            margin={{
              top: showLabels ? 28 : 8,
              bottom: 48,
              left: showLabels ? 36 : 8,
              right: showLabels ? 36 : 8,
            }}
            slots={showLabels ? getOutsidePieSlots(outerRadius) : undefined}
            slotProps={{
              ...getOutsidePieSlotProps(outerRadius),
              legend: {
                direction: "row",
                position: { vertical: "bottom", horizontal: "middle" },
                labelStyle: { fontSize: expanded ? 14 : 12 },
              },
            }}
          />
        );
      }}
    </ChartCard>
  );
};

export default ComplaintClassificationPieChart;
